// Full auth process. All actions in one. Good for managing, testing and scalability.

import {
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  deleteUser,
  signOut,
} from "firebase/auth";
import { auth } from "../app/firebase";
import { createUserInFirestore } from "./firestoreService"; //custom function for creating user document in the data base
import { isAdminWebEmail } from "../config/adminWebAccess";

// Note: Welcome and admin notification emails are now sent automatically
// via Firebase Cloud Function onUserCreate (functions/index.js)
// This ensures emails are sent reliably even if frontend fails

//creating user in firebase
export const signUpWithEmail = async (email, password) => {
  localStorage.setItem("justRegistered", "true"); //flag 'justRegistered'
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  await createUserInFirestore(userCredential.user);
  
  // Welcome and admin notification emails are sent automatically
  // via Firebase Cloud Function onUserCreate (functions/index.js)
};

//user login
export const signInWithEmail = async (email, password) => {
  localStorage.removeItem("justRegistered"); //removing flag 'justRegistered'
  return signInWithEmailAndPassword(auth, email, password);
};

// Helper function to process authentication result
const processAuthResult = async (result) => {
  if (!result || !result.user) {
    console.error("processAuthResult: Invalid result object");
    return;
  }
  
  let additionalInfo;
  try {
    additionalInfo = getAdditionalUserInfo(result);
  } catch (error) {
    console.error("processAuthResult: Error getting additional info:", error);
    return;
  }

  //if new user - adding flag 'justRegistered', if not - removing
  if (additionalInfo?.isNewUser) {
    // Set flag first - this is critical for redirect to work
    // This must be synchronous and happen immediately
    try {
      localStorage.setItem("justRegistered", "true");
    } catch (storageError) {
      console.error("processAuthResult: Error setting localStorage flag:", storageError);
    }
    
    // Create user in Firestore - if this fails, we still want emails to be sent
    try {
      await createUserInFirestore(result.user);
    } catch (firestoreError) {
      // Log error but continue - user is already created in Firebase Auth
      console.error("processAuthResult: Error creating user in Firestore:", firestoreError);
    }
    
    // Welcome and admin notification emails are sent automatically
    // via Firebase Cloud Function onUserCreate (functions/index.js)
  } else {
    localStorage.removeItem("justRegistered");
  }
};

//login and signin with OAuth popup (Google, Apple, …)
const signInWithOAuthProvider = async (provider) => {
  try {
    const result = await signInWithPopup(auth, provider);

    try {
      await processAuthResult(result);
    } catch (processError) {
      console.error("signInWithOAuthProvider: Error in processAuthResult:", processError);
    }
  } catch (error) {
    console.error("signInWithOAuthProvider: Error during authentication:", error);
    const isPopupClosed =
      error.code === 'auth/popup-closed-by-user' ||
      error.code === 'auth/cancelled-popup-request' ||
      error.code === 'auth/popup-blocked' ||
      error.message?.includes('popup') ||
      error.message?.includes('closed');

    if (isPopupClosed) {
      const popupClosedError = new Error("Popup closed by user");
      popupClosedError.code = "auth/popup-closed-by-user";
      throw popupClosedError;
    }
    throw error;
  }
};

//login and signin with google
export const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  
  // Configure provider to always show account picker (list of accounts)
  // This ensures users see the list of Google accounts instead of email/password form
  // Using multiple parameters to force account selection screen
  provider.setCustomParameters({
    prompt: 'select_account', // Always show account selection screen
    // Add additional scopes that might help with account selection
  });
  
  // Add email scope to help with account selection
  provider.addScope('email');
  provider.addScope('profile');
  
  await signInWithOAuthProvider(provider);
};

/** Google sign-in for the quiet admin entrance. Any other account is signed out. */
export const signInAdminWithGoogle = async () => {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  provider.addScope('email');
  provider.addScope('profile');

  const result = await signInWithPopup(auth, provider);
  const email = (result.user?.email || '').trim().toLowerCase();

  if (!isAdminWebEmail(email)) {
    const isNew = getAdditionalUserInfo(result)?.isNewUser;
    try {
      if (isNew && result.user) await deleteUser(result.user);
    } catch (error) {
      console.warn('signInAdminWithGoogle: could not delete rejected account', error);
    }
    await signOut(auth);
    const denied = new Error('Admin entrance is limited to one account');
    denied.code = 'auth/admin-only';
    throw denied;
  }

  await processAuthResult(result);
  return result;
};

  